/**
 * 例外を画面のエラーダイアログ（`lib/errors/report.ts`）に積む関数と、積むかどうかの判定（#456）。
 *
 * @remarks
 * **自動でダイアログに出すのは想定外のエラーだけ**（docs/logging.md）:
 * - API の 5xx・通信エラー・422（入力スキーマ違反。画面は送信前に検証しているので来たら不具合）
 *   … `customFetch` / `fetchWithAuth` が投げる時点で積む（{@link shouldAutoReportApiError}）
 * - 画面の実行時エラー（描画・イベントハンドラ・未処理の Promise）と、catch した先が
 *   コードの不具合を示す例外（{@link isProgrammingError}）だったとき
 *
 * 400・403・404・409 は画面ごとに想定内の扱い（確認の往復・権限の案内・削除済みの案内）が
 * あるので、今のトーストのままにし、トーストの「詳細」から同じダイアログを開けるようにする
 * （`components/errors/error-toast.ts`）。
 */
import { ApiError } from "@/lib/api/api-error";
import { type ErrorReportInput, reportError } from "@/lib/errors/report";

/** 通信エラー（サーバーに届かない・応答が無い）の説明。 */
const NETWORK_ERROR_MESSAGE =
  "サーバーに接続できませんでした（ネットワークの切断・タイムアウト・サーバーの停止など）";

/** コードの不具合を示す組み込みの例外（意図して投げる素の `Error` と区別する）。 */
const PROGRAMMING_ERRORS = [
  TypeError,
  ReferenceError,
  RangeError,
  SyntaxError,
  EvalError,
  URIError,
] as const;

/** 画面の実行時エラーとして積んだ例外（`window` のハンドラと catch 側で二重に積まない）。 */
const reportedErrors = new WeakSet<object>();

/**
 * API の失敗を自動でダイアログに出すか（5xx・通信エラー・422）。
 *
 * @param error - `customFetch` / `fetchWithAuth` が作った失敗。
 */
export function shouldAutoReportApiError(error: ApiError): boolean {
  return error.status === 0 || error.status >= 500 || error.status === 422;
}

/**
 * catch した例外がコードの不具合を示すか（TypeError・ReferenceError など）。
 *
 * @remarks
 * 画面は「…の取得に失敗しました」のような素の `Error` を意図して投げ、catch でトーストに
 * 出している。それらは利用者に見せる説明なので、ダイアログには出さない。
 *
 * @param error - catch した例外。
 */
export function isProgrammingError(error: unknown): boolean {
  return PROGRAMMING_ERRORS.some((ctor) => error instanceof ctor);
}

/**
 * 中断（画面遷移・`AbortController`）による失敗か。ダイアログにもログにも出さない。
 *
 * @param error - catch した例外。
 */
export function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

/**
 * API の失敗をダイアログの 1 件にする。
 *
 * @param error - API の失敗。
 */
export function apiErrorReportInput(error: ApiError): ErrorReportInput {
  const network = error.status === 0;
  const cause = error.cause;
  const causeText =
    cause instanceof Error ? `${cause.name}: ${cause.message}` : cause != null ? String(cause) : null;
  return {
    kind: network ? "network" : "api",
    message: network
      ? `${NETWORK_ERROR_MESSAGE}${causeText ? `（${causeText}）` : ""}`
      : (error.serverMessage ?? `${error.status} ${error.statusText}`),
    occurredAt: error.occurredAt,
    requestId: error.requestId,
    request: error.request ? { method: error.request.method, url: error.request.url } : null,
    status: error.status,
    statusText: error.statusText,
    code: error.code,
    serverTimestamp: error.serverTimestamp,
    serverStack: error.serverError?.stack ?? null,
    serverErrorType: error.serverError?.type ?? null,
    bodyExcerpt: error.bodyExcerpt,
  };
}

/**
 * API の失敗をダイアログに積む（積み済みなら何もしない）。
 *
 * @param error - API の失敗。
 */
export function reportApiError(error: ApiError): void {
  if (error.reported) return;
  error.reported = true;
  reportError(apiErrorReportInput(error));
}

/** 読み込みの失敗を表す `errorName`（ダイアログの種類の括弧に出る）。 */
const RESOURCE_LOAD_ERROR_NAME = "ResourceLoadError";

/**
 * Error でなく DOM のイベントで失敗した値を、ダイアログの説明にする（#794）。
 *
 * @remarks
 * React 19 はスタイルシートの `<link>` の `onerror` をそのまま Promise の reject に渡す
 * （catch されないので `unhandledrejection` に Event のまま届く）。`String(event)` では
 * 「[object Event]」になって何が起きたか分からないので、読み込めなかった部品の URL を出す。
 *
 * @param event - reject・throw されたイベント。
 */
function eventReportText(event: Event): { message: string; errorName: string } {
  if (event.type !== "error") {
    return { message: `画面の処理がイベント「${event.type}」で失敗しました`, errorName: "Event" };
  }
  // DOM の型（HTMLLinkElement 等）は単体テストの node 環境に無いので、持っている値で見分ける
  const target = event.target as { tagName?: unknown; href?: unknown; src?: unknown } | null;
  const tag = typeof target?.tagName === "string" ? target.tagName.toUpperCase() : "";
  const part = tag === "LINK" ? "（スタイルシート）" : tag === "SCRIPT" ? "（スクリプト）" : "";
  const url = [target?.href, target?.src].find(
    (value): value is string => typeof value === "string" && value !== "",
  );
  return {
    message: `画面の部品${part}の読み込みに失敗しました${url ? `（${url}）` : ""}。画面を再読み込みしてください`,
    errorName: RESOURCE_LOAD_ERROR_NAME,
  };
}

/**
 * 想定外の例外をダイアログに積む（API の失敗なら {@link reportApiError}）。
 *
 * @param error - 例外（Error 以外が投げられても受ける。DOM のイベントは読み込みの失敗として説明する）。
 * @param kind - 画面の処理中（`runtime`・既定）か描画中（`render`）か。
 * @param digest - サーバー側の描画で起きた例外の digest。
 */
export function reportUnexpectedError(
  error: unknown,
  kind: "runtime" | "render" = "runtime",
  digest: string | null = null,
): void {
  if (error instanceof ApiError) {
    reportApiError(error);
    return;
  }
  if (typeof error === "object" && error !== null) {
    if (reportedErrors.has(error)) return;
    reportedErrors.add(error);
  }
  if (typeof Event !== "undefined" && error instanceof Event) {
    reportError({ kind, ...eventReportText(error), clientStack: null, digest });
    return;
  }
  const err = error instanceof Error ? error : null;
  reportError({
    kind,
    message: err ? err.message || err.name : String(error),
    errorName: err?.name ?? null,
    clientStack: err?.stack ?? null,
    digest,
  });
}

/**
 * トーストの「詳細」から、その失敗をダイアログに出す（ログは送らない・BE のログにある）。
 *
 * @param error - API の失敗。
 */
export function openApiErrorDetails(error: ApiError): void {
  reportError(apiErrorReportInput(error), { notify: false });
}
